import { Fragment, useMemo, useRef, useState } from 'react';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import { listLots, repriceLots, repriceErrorMessage } from '../../api/inventory';
import { availableLots } from '../../utils/lots';
import { formatStock } from '../../utils/soldBy';
import form from '../../styles/form.module.css';
import styles from './ProductFormDrawer.module.css';

function paise(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function moneyOrNull(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function sellAboveMrp(sell, mrp) {
  const left = paise(sell);
  const right = paise(mrp);
  if (left == null || right == null) return false;
  return left > right;
}

function formatWhen(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ShelfLotsEditor({
  lots,
  loading,
  loadError,
  soldBy,
  unit,
  catalogPrice,
  productId,
  token,
  onLotsChange,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [shelfPrice, setShelfPrice] = useState('');
  const [shelfMrp, setShelfMrp] = useState('');
  const [shelfCost, setShelfCost] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const openLots = useMemo(() => availableLots(lots), [lots]);
  const selected = openLots.filter((lot) => selectedIds.includes(lot.lotId));
  const sell = moneyOrNull(shelfPrice);
  const nextMrp = shelfMrp === '' ? null : moneyOrNull(shelfMrp);
  const nextCost = shelfCost === '' ? null : moneyOrNull(shelfCost);
  const mrpFieldInvalid = shelfMrp !== '' && nextMrp == null;
  const costFieldInvalid = shelfCost !== '' && nextCost == null;
  const catalog = moneyOrNull(catalogPrice);
  const blocked = selected.filter((lot) => {
    if (sell == null) return false;
    const limit = nextMrp != null ? nextMrp : lot.mrp;
    return sellAboveMrp(sell, limit);
  });
  const canUpdate = selected.length > 0
    && selected.length <= 25
    && sell != null
    && !mrpFieldInvalid
    && !costFieldInvalid
    && blocked.length === 0
    && !saving;

  const toggle = (lotId) => {
    setNotice('');
    setError('');
    setSelectedIds((prev) => (
      prev.includes(lotId) ? prev.filter((id) => id !== lotId) : [...prev, lotId]
    ));
  };

  const useCatalogPrice = () => {
    if (catalog == null) return;
    setShelfPrice(String(catalog));
    setError('');
    setNotice('');
  };

  const updateShelf = async () => {
    if (!canUpdate || !productId || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await repriceLots(productId, {
        sellingPrice: sell,
        mrp: nextMrp == null ? undefined : nextMrp,
        costPrice: nextCost == null ? undefined : nextCost,
        lotIds: selected.map((lot) => lot.lotId),
      }, token);
      onLotsChange(result.lots || []);
      setSelectedIds([]);
      setShelfPrice('');
      setShelfMrp('');
      setShelfCost('');
      const count = selected.length;
      setNotice(`Shelf price is now ${formatCurrency(sell)} on ${count} lot${count === 1 ? '' : 's'}.`);
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'lot_mismatch' || code === 'lot_not_open') {
        try {
          const data = await listLots(productId, token);
          onLotsChange(data.lots || []);
          setSelectedIds([]);
        } catch {
          // Show the reprice error either way.
        }
      }
      setError(repriceErrorMessage(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onPriceKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    updateShelf();
  };

  return (
    <div className={styles.lotsSection}>
      <p className={styles.shelfHeading}>Open lots (shelf)</p>
      <p className={form.hint}>
        POS charges these prices. Check the lots to change, then enter the new price. Quantity stays the same.
      </p>
      {loading && <p className={form.hint}>Loading lots…</p>}
      {loadError && <p className={styles.lotsError}>{loadError}</p>}
      {notice && <p className={styles.shelfNotice}>{notice}</p>}
      {!loading && !loadError && openLots.length === 0 && (
        <p className={form.hint}>No open lots yet. Receive stock from Inventory → Stock In.</p>
      )}
      {openLots.length > 0 && (
        <div className={styles.lotsTableWrap}>
          <table className={styles.lotsTable}>
            <thead>
              <tr>
                <th className={styles.lotCheckCol} aria-label="Select" />
                <th>{soldBy === 'WEIGHT' ? 'Sell / kg' : 'Sell'}</th>
                <th>MRP</th>
                <th>{soldBy === 'WEIGHT' ? 'Kg' : 'Qty'}</th>
                <th>Cost</th>
                <th>Source</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {openLots.map((lot) => {
                const checked = selectedIds.includes(lot.lotId);
                const limit = nextMrp != null ? nextMrp : lot.mrp;
                const blockedRow = checked && sell != null && sellAboveMrp(sell, limit);
                const received = formatWhen(lot.receivedAt);
                const rowClass = blockedRow
                  ? styles.lotRowBlocked
                  : checked
                    ? styles.lotRowSelected
                    : '';
                return (
                  <Fragment key={lot.lotId}>
                    <tr className={rowClass}>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked}
                          aria-label={`Select lot at ${formatCurrency(Number(lot.sellingPrice) || 0)}`}
                          onChange={() => toggle(lot.lotId)}
                        />
                      </td>
                      <td>
                        {soldBy === 'WEIGHT'
                          ? `${formatCurrency(Number(lot.sellingPrice) || 0)} / ${unit || 'kg'}`
                          : formatCurrency(Number(lot.sellingPrice) || 0)}
                      </td>
                      <td>{lot.mrp != null && lot.mrp !== '' ? formatCurrency(Number(lot.mrp) || 0) : '—'}</td>
                      <td>{formatStock(lot.qty, { soldBy, unit })}</td>
                      <td>{lot.costPrice != null && lot.costPrice !== '' ? formatCurrency(Number(lot.costPrice) || 0) : '—'}</td>
                      <td>{lot.source || '—'}</td>
                      <td>{received || '—'}</td>
                    </tr>
                    {blockedRow && (
                      <tr className={styles.lotRowWarn}>
                        <td colSpan={7}>
                          {formatCurrency(sell)} is above this lot’s MRP of {formatCurrency(Number(limit) || 0)}. Enter an MRP of at least {formatCurrency(sell)}, or uncheck this lot.
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected.length > 0 && (
        <div className={styles.shelfEditor}>
          <p className={styles.shelfEditorTitle}>
            New price for {selected.length} lot{selected.length === 1 ? '' : 's'}
          </p>
          <p className={form.hint}>Unchecked lots keep their current price.</p>
          <label className={form.field}>
            <span className={form.label}>New selling price</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input
                className={form.input}
                type="number"
                min="0"
                step="0.01"
                aria-label="New selling price"
                value={shelfPrice}
                onChange={(event) => {
                  setShelfPrice(event.target.value);
                  setError('');
                  setNotice('');
                }}
                onKeyDown={onPriceKeyDown}
                placeholder="e.g. 54"
              />
            </div>
          </label>
          {catalog != null && paise(shelfPrice) !== paise(catalog) && (
            <button type="button" className={styles.useCatalogBtn} onClick={useCatalogPrice}>
              Use catalog price {formatCurrency(catalog)}
            </button>
          )}
          <label className={form.field}>
            <span className={form.label}>MRP for these lots</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input
                className={form.input}
                type="number"
                min="0"
                step="0.01"
                aria-label="MRP for these lots"
                value={shelfMrp}
                onChange={(event) => {
                  setShelfMrp(event.target.value);
                  setError('');
                  setNotice('');
                }}
                onKeyDown={onPriceKeyDown}
                placeholder="Leave blank to keep each MRP"
              />
            </div>
            <span className={form.hint}>Leave this blank and each lot keeps its own MRP.</span>
          </label>
          <label className={form.field}>
            <span className={form.label}>Cost for these lots</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input
                className={form.input}
                type="number"
                min="0"
                step="0.01"
                aria-label="Cost for these lots"
                value={shelfCost}
                onChange={(event) => {
                  setShelfCost(event.target.value);
                  setError('');
                  setNotice('');
                }}
                onKeyDown={onPriceKeyDown}
                placeholder="Leave blank to keep each cost"
              />
            </div>
            <span className={form.hint}>Leave this blank and each lot keeps its own cost. The product cost is not changed.</span>
          </label>
          {selected.length > 25 && (
            <p className={styles.lotsError}>You can update at most 25 lots at once.</p>
          )}
          {mrpFieldInvalid && <p className={styles.lotsError}>Enter an MRP of ₹0 or more, or leave it blank.</p>}
          {costFieldInvalid && <p className={styles.lotsError}>Enter a cost of ₹0 or more, or leave it blank.</p>}
          {error && <p className={styles.lotsError}>{error}</p>}
          <Button type="button" onClick={updateShelf} disabled={!canUpdate}>
            {saving ? 'Updating…' : 'Update shelf price'}
          </Button>
        </div>
      )}
    </div>
  );
}
